import React, {useMemo, useState} from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {ClientDto} from '../dtos/client.dto';
import {ClientContactDto} from '../dtos/client-contact.dto';
import {addressOneLine} from '../../../shared/components/list-page/list-format';
import {addressLine, displayTitle} from './contact-entries';
import {searchClients} from './client-search';

/**
 * Add New Appointment's "Search existing clients" (SQ-666), Android's
 * ClientSearchField: the label stays on the border like every field on the
 * card, the hint is what it matches on (or, once one is picked, that
 * client's name), and the matches list under it. Picking one clears the
 * field and hands the client up; the card below then fills with them.
 */
export const ClientSearchField = ({clients, contacts, linkedName, onPick}: {
  clients: ClientDto[];
  contacts: ClientContactDto[];
  linkedName?: string;
  onPick: (clientId: number) => void;
}) => {
  const [query, setQuery] = useState('');
  const hits = useMemo(() => searchClients(query, clients, contacts), [query, clients, contacts]);
  return (
    <div className="cc-search">
      <label className="cc-field">
        <span className="cc-label cc-label--page">Search existing clients</span>
        <SearchIcon className="cc-search-icon"/>
        <input className="cc-input cc-search-input" value={query} placeholder={linkedName || 'Name, phone, email or address'}
               onChange={e => setQuery(e.target.value)} aria-label="Search existing clients"/>
      </label>
      {hits.length > 0 && (
        <ul className="cc-search-hits" role="listbox" aria-label="Matching clients">
          {hits.map(({client, matched}) => {
            // Found through an additional entry: say which, since nothing else on the line would explain the match.
            const line = matched
              ? `${displayTitle(matched)} · ${matched.kind === 'address' ? addressLine(matched) : matched.value}`
              : addressOneLine(client.address, client.city, null);
            return (
              <li key={client.id} role="option" aria-selected={false} className="cc-search-hit"
                  onClick={() => { setQuery(''); onPick(client.id); }}>
                <div className="cc-search-hit-name">{client.name}</div>
                {line && <div className="cc-search-hit-line">{line}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
